import {makeAutoObservable} from "mobx";
import IconCircleRestockable from "../../../../components/icons/icon-circle-restockable/icon-circle-restockable";
import IconCircleReturned from "../../../../components/icons/icon-circle-returned/icon-circle-returned";
import {IUploadMediaFile} from "../../../../components/media-upload/media-upload";
import {FEATURES} from "../../../../models/Plan";
import currentPortalStore from "../../../../stores/current-portal-store";
import {RETURN_METHOD_TYPE} from "../../../return-methods/return-method-types";
import ValidateItemRowStore, {IValidateItemRow} from "../components/rma-product-case-drawer/validate-items/validate-item-row-store";
import {
    IRmaProductCase,
    IValidateItemsActionRequest,
    IValidationImageUpload,
    SHIPMENT_STATUS_ENUM,
    IUploadedMediaFile,
    CANT_REJECT_ITEM_REASONS
} from "../rma-types";
import CustomerCaseStore from "./customer-case-store";
import ProductCaseItemsBalanceStore from "./product-case-items-balance-store";
import QuestionStore from "./question-store";
import RmaExchangeStore from "./rma-exchange-store";
import RmaOfferStore from "./rma-offer-store";
import RmaPageStore from "./rma-page-store";
import RmaProductStore from "./rma-product-store";
import RmaReturnReasonStore from "./rma-return-reason-store";
import RmaShipmentStore from "./rma-shipment-store";

class ProductCaseStore {
    private _model: IRmaProductCase = {} as IRmaProductCase;
    private _productStore: RmaProductStore = {} as RmaProductStore;
    private _returnReasonStore: RmaReturnReasonStore = {} as RmaReturnReasonStore;
    private _rmaOfferStore: RmaOfferStore = {} as RmaOfferStore;
    private _questionsStores: QuestionStore[] = [];
    private _exchangeStore?: RmaExchangeStore;
    private _validationReturnedStore: ValidateItemRowStore = {} as ValidateItemRowStore;
    private _validationCanBeRestockedStore: ValidateItemRowStore = {} as ValidateItemRowStore;
    private _itemsBalanceStore: ProductCaseItemsBalanceStore = {} as ProductCaseItemsBalanceStore;
    private _isValidationInEditMode: boolean = false;
    private _validationImagesToUpload: IValidationImageUpload[] = [];

    constructor(model: IRmaProductCase, private _rmaPageStore: RmaPageStore, private _customerCaseStore: CustomerCaseStore) {
        makeAutoObservable(this, {}, { autoBind:true });
        this._setModel(ensureValidModel(model));
        this._productStore = new RmaProductStore(model.product);
        this._returnReasonStore = new RmaReturnReasonStore(model.merchant_return_reason);
        this._rmaOfferStore = new RmaOfferStore(this, model.product_case_offers[0]);
        this._questionsStores = model.questions
            .filter(question => question.answers.length > 0 || question.images.length > 0)
            .map(question => new QuestionStore(question));
        if(model.exchange) {
            this._exchangeStore = new RmaExchangeStore(model.exchange, this._customerCaseStore);
        }
        if(model._items_balance) {
            this._itemsBalanceStore = new ProductCaseItemsBalanceStore(model._items_balance, this, _customerCaseStore);
        }
        this._setValidateItemsStores();
    }

    private _setModel(model: IRmaProductCase) {
        this._model = model;
    }

    private _setValidateItemsStores(){
        this._validationReturnedStore = new ValidateItemRowStore(this._getItemReturnedModel(), this._onIsReturnedChanged);
        this._validationCanBeRestockedStore = new ValidateItemRowStore(this._getItemCanBeRestockedModel());
    }

    public get customerCaseStore(): CustomerCaseStore { return this._customerCaseStore; }
    public get productStore(): RmaProductStore { return this._productStore; }
    public get exchangeStore(): RmaExchangeStore | undefined { return this._exchangeStore; }
    public get returnReasonStore(): RmaReturnReasonStore { return this._returnReasonStore; }
    public get rmaOfferStore(): RmaOfferStore { return this._rmaOfferStore; }
    public get questionsStores(): QuestionStore[] { return this._questionsStores; }
    public get shipmentId(): number | undefined { return this._model.shipment_id; }
    public get paidPrice(): number { return this._model.paid_price; /*Already in payment currency*/ }
    public get isValidated(): boolean { return isSet(this._model.is_returned) || isSet(this._model.is_restockable) }
    public get id(): number { return this._model.id; }
    public get isReturned(): boolean | null { return this._model.is_returned; }
    public get isRestockable(): boolean | null { return this._model.is_restockable; }
    public get _isRestockable(): boolean | null { return this._model._is_restockable; }
    public get isRestocked(): boolean | null { return this._model._is_restocked; }
    public get restockedPlatforms(): string | null { return this._model._restocked_platforms;}
    public get isRefunded(): boolean | null { return this._model._is_refunded; }
    public get isSuccessfullyRefunded(): boolean { return this._model._is_successfully_refunded; }
    public get refundedAmount(): number { return this._model._refunded_amount; }
    public get bankRefundedAmount(): number { return this._model._bank_refunded_amount; }
    public get externalRefundAmount(): number { return this._model._external_refund_amount; }
    public get isAutomaticallyRefunded(): boolean { return this._model._is_automatically_refunded; }
    public get isCredited(): boolean | null { return !!this._model._credited_amount; }
    public get creditedAmount(): number { return +this._model._credited_amount; }
    public get automaticallyCreditedAmount(): number { return +this._model._automatically_credited_amount; }
    public get isRefundedToGiftCard(): boolean | null { return !!this._model._gifted_amount; }
    public get giftedAmount(): number { return +this._model._gifted_amount; }
    public get automaticallyGiftedAmount(): number { return +this._model._automatically_gifted_amount; }
    public get uploadedMediaFiles(): IUploadedMediaFile[] { return this._model._uploaded_media_files; }
    public get automaticallyCredited(): boolean { return this._model._automatically_credited; }
    public get automaticallyGifted(): boolean { return this._model._automatically_gifted; }
    public get isPartOfBxGyDiscount(): boolean { return !!this._model._bxgy_discount_data; }
    public get isBx(): boolean { return !!this._model?._bxgy_discount_data?.is_buy; }
    public get isGy(): boolean { return !!this._model?._bxgy_discount_data?.is_get; }
    public get bxgyDiscountCode(): string { return this._model._bxgy_discount_data?.discount_code ?? ''; }
    public get isPartOfCompareAtPriceDiscount(): boolean { return !!this._model._cap_discount_data; }

    public get validationReturnedStore(): ValidateItemRowStore { return this._validationReturnedStore; }
    public get validationCanBeRestockedStore(): ValidateItemRowStore { return this._validationCanBeRestockedStore; }
    public get itemsBalanceStore(): ProductCaseItemsBalanceStore { return this._itemsBalanceStore; }
    public get isValidationInEditMode(): boolean { return this._isValidationInEditMode; }
    public get returnMethodName(): string { return this._model.return_method._internal_name; }
    public get requiresShipping(): boolean{ return this._model.return_method.type !== RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED; }
    public get isSwitchToExchange(): boolean { return this._model.has_incentive_from_switch_to_exchange; }

    public get isRejected(): boolean { return this._model.is_rejected; }
    public get rejectedReason(): string | null { return this._model.rejected_reason; }
    public get canBeRejected(): boolean { return this._model._can_reject?.result ?? false; }
    public get canNotBeRejectedReason(): CANT_REJECT_ITEM_REASONS|null { return this._model._can_reject?.reason; }

    /** get shipment from RMA by Product Case shipment_id */
    private get _shipment(): RmaShipmentStore | undefined {
        return this._rmaPageStore.shipmentList.find(shipment => shipment.id === this.shipmentId);
    }

    public get isChecked(): boolean { return this._rmaPageStore.productCasesStateMap[this.id].checked; }

    public setIsChecked(v: boolean) {
        // mutate map on rmapage store so that it will be repopulated on next instantiation
        this._rmaPageStore.productCasesStateMap[this.id].checked = v;
    }

    public get isHiddenResolution(): boolean { return this._model._is_hidden_resolution; }

    public get isExchange(): boolean {
        return !!this._exchangeStore;
    }

    /*
     * _model.answers is a string of strings separated by ">":
     *
     *   "> first answer
     *    > another answer"
     *
     *    OR
     *
     *   ">
     *    > another answer"
     *
     *    OR
     *
     *   ">
     *    > "
     *
     *    etc.
     *
     * where:
     * - manual exchange message is ALWAYS the first one
     * - manual exchange cannot be empty, but can consist of spaces
     *
     * in order to extract it we split the string by ">"
     * filter empty entries to ensure only real messages are in
     * trim spaces around each message
     * return the first list item
     *
    */
    public getManualExchangeMessage(): string {
        if (!isSet(this._model.answers)) {
            return '';
        }

        return this._model.answers?.split('>')
            .filter(str => str)
            .map((str) => str.trim())?.[0] ?? '';
    }

    public hasUploadedMediaFiles(): boolean {
        return this._model._uploaded_media_files.length > 0;
    }

    /*
	 * Status codes may NOT be arranged according to the stages of the shipment delivery process.
     * So we need to check the status explicitly to determine whether shipment is in certain state.
     */
	public hasInTransitShipment(): boolean {
        if (!this._shipment) {
            return false;
        }

		return [
            SHIPMENT_STATUS_ENUM.SHIPPED,
            SHIPMENT_STATUS_ENUM.OUT_FOR_DELIVERY,
        ].includes(this._shipment?.statusCode);
	}

    private _onIsReturnedChanged() {
        this._validationCanBeRestockedStore.setChecked(false);
    }

    private _getItemReturnedModel(): IValidateItemRow {
        return {
            comment: this._model.returned_comment,
            icon: IconCircleReturned,
            is_checked: this._model.is_returned !== null ? this._model.is_returned : true,
            label: 'ITEM_WAS_RETURNED'
        };
    }

    private _getItemCanBeRestockedModel(): IValidateItemRow {
        return {
            comment: this._model.restocked_comment,
            icon: IconCircleRestockable,
            is_checked: this._model.is_restockable !== null ? this._model.is_restockable : true,
            label: 'CAN_BE_RESTOCKED'
        };
    }

    setValidateItemEditMode(value: boolean) {
        this._isValidationInEditMode  = value;
    }

    public validationImages(): IUploadMediaFile[]{
        return this._model.validation_images.filter(i => !i._is_deleted).map(i=>{
            return {
                src: i._full_url,
                imageNotAvailable: i.is_saved
            }
            }).concat(this._validationImagesToUpload.map(i=> {
                return {
                    src: i.base64FileData,
                    imageNotAvailable: false,
                };
            }));
    }

    public onUploadValidationImage(fileData: File, fileContent: string | ArrayBuffer | null | undefined){

        if(fileContent) {
            this._validationImagesToUpload.push({
                filename: fileData.name,
                product_case_validation_id: this._model.id,
                type: fileData.type,
                base64FileData: (fileContent as string),
            });
        }
    }

    public onDeleteValidationImage(mediaFile: IUploadMediaFile){
        /*
        * Delete from images already uploaded
        *  */
        const imageAlreadyUploaded = this._model.validation_images.find(mf =>
            mf._full_url === mediaFile.src
        );
        if(imageAlreadyUploaded){
            imageAlreadyUploaded._is_deleted = true;
        }
        /*
        * Delete from images to upload
        *  */
        this._validationImagesToUpload = this._validationImagesToUpload.filter(mf => mf.base64FileData !== mediaFile.src);
    }

    public wasReceived(): boolean {
        return this._shipment?.statusCode === SHIPMENT_STATUS_ENUM.RECEIVED;
    }


    public cancelValidation(){
        this.setValidateItemEditMode(false);
        /** discards newly uploaded images */
        this._validationImagesToUpload = [];
        /** previously uploaded imges if where marked as deleted - the mark is discraded */
        this._model.validation_images.map(i=>i._is_deleted = false);
        this._setValidateItemsStores();
    }

    public getValidationRequestModel(): IValidateItemsActionRequest {
        return {
            images_to_upload:this._validationImagesToUpload,
            images_to_delete:this._model.validation_images.filter(i => i._is_deleted === true),
            is_returned: this._validationReturnedStore.isChecked,
            returned_comment: !this._validationReturnedStore.isChecked ? this._validationReturnedStore.comment : '',
            is_restockable: this._validationCanBeRestockedStore.isChecked,
            restocked_comment: !this._validationCanBeRestockedStore.isChecked ? this._validationCanBeRestockedStore.comment : '',
            product_case_id: this._model.id
        } ;
    }

    public showValidationChip(): boolean {
        return currentPortalStore.isFeatureAllowed(FEATURES.VALIDATE_ITEMS) && !!currentPortalStore.setting?.isValidationEnabled && this.wasReceived();
    }
}
function ensureValidModel(model: IRmaProductCase){
    const toNullableBool = (v: any) => v === null ? null : Boolean(v);
    const toBool = (v: any) => Boolean(v);

    return {
        ...model,
        is_rejected: toBool(model.is_rejected),
        paid_price: Number(model.paid_price),
        is_returned: toNullableBool(model.is_returned),
        is_restockable: toNullableBool(model.is_restockable),
        // Not in use, see _is_restocked - it's computed and is more accurate
        is_restocked: toNullableBool(model.is_restocked),
        _refunded_amount: Number(model._refunded_amount),
        _bank_refunded_amount: Number(model._bank_refunded_amount),
        _external_refund_amount: Number(model._external_refund_amount),
        has_incentive_from_switch_to_exchange: toBool(model.has_incentive_from_switch_to_exchange),
    }
}

const isSet = (v: any): boolean => {
    return v !== undefined && v !== null;
}

export default ProductCaseStore;